import { useState, useEffect } from "react";
import { useAccessToken } from "./useAuthentication";
import { backendUrl } from "../constants";
import { authedFetch } from "../lib/authedFetch";

interface CheckpointAccessResponse {
  has_access: boolean;
  actions: string[];
  user: string;
}

export const useCheckpointAccess = () => {
  const { accessToken } = useAccessToken();
  const [hasCheckpointAccess, setHasCheckpointAccess] = useState<boolean | null>(null);
  const [checkpointActions, setCheckpointActions] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const checkCheckpointAccess = async () => {
    if (!accessToken) {
      setHasCheckpointAccess(false);
      setCheckpointActions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authedFetch(`${backendUrl}/vpc-api/checkpoint-access`);

      if (!response.ok) {
        if (response.status === 401) {
          setError("Authentication failed. Please log in again.");
        } else if (response.status === 403) {
          setError("Access forbidden. You may not have permission to access this resource.");
        } else {
          setError(`Server error: ${response.status}`);
        }
        setHasCheckpointAccess(false);
        setCheckpointActions([]);
        return;
      }

      const data: CheckpointAccessResponse = await response.json();
      setHasCheckpointAccess(data.has_access);
      setCheckpointActions(data.actions || []);
    } catch (err) {
      console.error("Error checking checkpoint access:", err);
      setError(err instanceof Error ? err.message : "Unknown error occurred");
      setHasCheckpointAccess(false);
      setCheckpointActions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      checkCheckpointAccess();
    } else {
      setHasCheckpointAccess(false);
      setCheckpointActions([]);
      setError(null);
    }
  }, [accessToken]);

  return {
    hasCheckpointAccess,
    checkpointActions,
    loading,
    error,
    checkCheckpointAccess,
  };
};